<template>
    <pendo-multiselect
        v-if="shouldShowChooser"
        v-model="selectedOption"
        class="multiselect-filter-apps"
        :labels="labelsConfig"
        :append-to-body="appendToBody"
        :disabled="disabled || hasErrored"
        :trigger-size="triggerSize"
        :group-label-key="groupLabelKey"
        :group-options-key="groupOptionKey"
        :internal-search="internalSearch"
        :invalid="invalid"
        label-key="displayName"
        :loading="isLoadingOptions || loading"
        :options="options"
        :placeholder="localizedPlaceholder"
        :searchable="searchable"
        :max-trigger-width="maxTriggerWidth"
        :full-width="fullWidth"
        :popper-options="popperOptions"
        value-key="id"
        @open="onOpen">
        <template
            #trigger
            v-if="useDataMeasureTrigger">
            <pendo-data-measure-trigger />
        </template>
        <template #header>
            <slot name="header" />
        </template>
        <template #option="{ option }">
            <div class="option-wrapper">
                <pendo-app-display
                    :apps="option"
                    class="app-name" />
            </div>
        </template>
        <template #selectedLabel="{ option }">
            <pendo-app-display
                :apps="option"
                class="app-name" />
        </template>
    </pendo-multiselect>
</template>

<script>
/**
 * NOTE: This component should be used in siutations where the app chooser does not
 * need to modify the global selectedApp in Vuex. This component simply emits an event
 * when a new app is selected. The parent component can listen for that event and act
 * accordingly.
 **/

import find from 'lodash/find';
import { mapState as mapPiniaState, mapActions as mapPiniaActions } from 'pinia';
import { useAppsStore } from '@/stores/apps.store';
import get from 'lodash/get';
import remove from 'lodash/remove';
import { AppChooserService } from '@/common/app-chooser/app-chooser.service';
import { PendoMultiselect, PendoAppDisplay, PendoDataMeasureTrigger } from '@pendo/components-vue3';
import { isMobileApp } from '@/utils/apps';

export default {
    name: 'LocalAppChooser',
    components: {
        PendoMultiselect,
        PendoAppDisplay,
        PendoDataMeasureTrigger,
    },
    props: {
        labelsConfig: {
            type: Object,
            default: () => ({}),
        },
        searchable: {
            type: Boolean,
            default: () => undefined,
        },
        internalSearch: {
            type: Boolean,
            default: () => undefined,
        },
        invalid: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
        modelValue: {
            type: [String, Number],
            default: undefined,
        },
        excludeGroups: {
            type: Boolean,
            default: false,
        },
        platform: {
            type: [String, Array],
            default: undefined,
        },
        enabledMap: {
            type: Object,
            default: () => ({}),
        },
        filterNonIntegratedApps: {
            type: Boolean,
            default: false,
        },
        placeholder: {
            type: String,
            default: undefined,
        },
        appendToBody: {
            type: Boolean,
            default: false,
        },
        removeRestrictedApps: {
            type: Boolean,
            default: false,
        },
        disabledOptionsMap: {
            type: Object,
            default: undefined,
        },
        optionsOverride: {
            type: Array,
            default: undefined,
        },
        popperOptions: {
            type: Object,
            default: undefined,
        },
        maxTriggerWidth: {
            type: Number,
            default: undefined,
        },
        triggerSize: {
            type: String,
            default: undefined,
        },
        useDataMeasureTrigger: {
            type: Boolean,
            default: false,
        },
        fullWidth: {
            type: Boolean,
            default: false,
        },
        excludeMobileApps: {
            type: Boolean,
            default: false,
        },
        showWhenSingleApp: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['update:modelValue', 'loadError', 'open'],
    data () {
        return {
            isLoadingOptions: true,
            hasErrored: false,
            options: [],
        };
    },
    computed: {
        ...mapPiniaState(useAppsStore, {
            isRestrictedMobileApp: 'isRestrictedMobileApp',
            isMultiApp: 'isMultiApp',
        }),
        groupLabelKey () {
            return this.excludeGroups ? '' : 'group';
        },
        groupOptionKey () {
            return this.excludeGroups ? '' : 'apps';
        },
        totalFilteredApps () {
            if (this.excludeGroups) {
                return this.options.length;
            }

            return this.options.reduce((total, group) => total + (group.apps?.length ?? 0), 0);
        },
        shouldShowChooser () {
            if (this.optionsOverride) {
                return true;
            }

            if (this.showWhenSingleApp) {
                return this.isLoadingOptions || this.totalFilteredApps > 0;
            }

            return this.isMultiApp;
        },
        localizedPlaceholder () {
            return this.placeholder || this.$t('localAppChooser.localAppChooserPlaceholder');
        },
        selectedOption: {
            get () {
                if (!this.modelValue) return '';
                if (this.optionsOverride) {
                    const indexForApp = this.optionsOverride.length - 1;

                    return find(this.optionsOverride[indexForApp].apps, (app) => app.id === this.modelValue);
                }

                const option = { ...AppChooserService.getOptionByAppId(this.modelValue) };
                delete option.icon;

                return option;
            },
            set (selectedOption) {
                if (selectedOption) {
                    this.$emit('update:modelValue', selectedOption.id);
                }
            },
        },
    },
    watch: {
        enabledMap () {
            this.loadOptions();
        },
        disabledOptionsMap () {
            this.loadOptions();
        },
    },
    async created () {
        try {
            await this.fetchApps();
            this.selectedOption = await AppChooserService.loadPredefinedAppOption(this.selectedOption);
            this.loadOptions();
            this.autoSelectIfSingle();
        } catch (error) {
            this.$emit('loadError', error);
            this.hasErrored = true;
        }

        this.isLoadingOptions = false;
    },
    methods: {
        ...mapPiniaActions(useAppsStore, ['loadAll']),
        fetchApps () {
            return this.loadAll();
        },
        loadOptions () {
            // optionsOverride is needed for a specific use case on the /setup page where the appchooser is used.
            // In this scenario, the page may be accessed publically, so we won't be able to rely on the internal
            // functions in the app chooser that fetch apps and format the multiselect options. optionsOverride
            // represents passed in options that are used instead of the internally fetched app options
            const rawGroups = this.optionsOverride
                ? this.optionsOverride
                : AppChooserService.formatOptions({
                    platform: this.platform,
                    excludeGroups: this.excludeGroups,
                    enabledMap: this.enabledMap,
                    filterNonIntegratedApps: this.filterNonIntegratedApps,
                });

            // Fix what gets returned from AppChooserService to be compatible with pendo-multiselect.
            const processedGroups = rawGroups.map((group) => ({
                ...group,
                apps: group.apps?.reduce((options, originalOption) => {
                    // The old dropdown would filter out disabled items.
                    if (!originalOption.$isDisabled) {
                        const option = { ...originalOption };
                        delete option.$isDisabled;
                        delete option.icon;
                        option.disabled = get(this.disabledOptionsMap, String(option.id), false);

                        if (!(this.excludeMobileApps && isMobileApp(option?.platform))) {
                            options.push(option);
                        }
                    }

                    return options;
                }, []) ?? [],
            }));

            if (this.removeRestrictedApps) {
                const appsSelector = `[${this.excludeGroups ? '0' : '1'}].apps`;
                remove(get(processedGroups, appsSelector), (app) => this.isRestrictedMobileApp(app.id));
            }

            this.options = this.excludeGroups ? (processedGroups[0]?.apps ?? []) : processedGroups;
        },
        autoSelectIfSingle () {
            if (!this.showWhenSingleApp || this.totalFilteredApps !== 1) return;

            const firstApp = this.excludeGroups
                ? this.options[0]
                : this.options.find((group) => group.apps?.length)?.apps[0];

            if (firstApp) {
                this.$emit('update:modelValue', firstApp.id);
            }
        },
        onOpen () {
            this.$emit('open');
        },
    },
};
</script>
<style scoped lang="less">
.segment-chooser-option__aside {
    color: var(--text-body-subtle-base);
    width: 100px;
    margin-left: 15px;
}

.app-name {
    width: 100%;
    &.nonIntegrated {
        width: 70%;
    }
}

.option-wrapper {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
}
</style>
